<template>
  <div
    class="rich-text-editor"
    :class="{ 'rich-text-editor--resizable': resizable }"
  >
    <VueEditor
      :value="value"
      :disabled="disabled"
      :placeholder="placeholder"
      :editor-toolbar="toolbar"
      :use-custom-image-handler="allowImages"
      @input="onInput"
      @blur="$emit('blur')"
      @ready="onReady"
      @image-added="(...args) => $emit('image-added', ...args)"
      @image-removed="$emit('image-removed', $event)"
    />
    <div
      v-if="linkTip"
      class="rich-text-editor__tip"
      :class="{ 'rich-text-editor__tip--above': linkTip.above }"
      :style="tipStyle"
      @mouseenter="cancelTipHide"
      @mouseleave="hideLinkTip"
    >
      <span class="rich-text-editor__tip-url">{{ linkTip.label }}</span>
      <a
        :href="linkTip.url"
        target="_blank"
        rel="noopener noreferrer"
        class="rich-text-editor__tip-open"
        title="Open in new tab"
      >
        <b-icon
          icon="box-arrow-up-right"
          font-scale="0.9"
        />
      </a>
    </div>
    <TextInputModal
      ref="linkModal"
      :modal-id="`rich-text-link-${_uid}`"
      title="Link URL"
      placeholder="https://"
      size="md"
      @save="applyLink"
    />
  </div>
</template>

<script>
import { VueEditor, Quill } from 'vue2-editor';
import TextInputModal from '@frontend/components/common/TextInputModal.vue';
import {
  IMAGE_TYPE_TAG_BLOT,
  IMAGE_TYPE_TAG_CLASS,
  imageTypeTagValueFromNode,
  registerImageTypeBlot,
} from '@frontend/components/briefs/briefImageTypeTag';

registerImageTypeBlot();

const LinkBlot = Quill.import('formats/link');
const Delta = Quill.import('delta');

const icons = Quill.import('ui/icons');
icons.unlink = [
  '<svg viewbox="0 0 18 18">',
  '<path class="ql-even ql-stroke" d="M8.9,4.577a3.476,3.476,0,0,1,.36,4.679A3.476,3.476,0,0,1,4.577,8.9C3.185,7.5,2.035,6.4,4.217,4.217S7.5,3.185,8.9,4.577Z"></path>',
  '<path class="ql-even ql-stroke" d="M13.423,9.1a3.476,3.476,0,0,0-4.679-.36,3.476,3.476,0,0,0,.36,4.679c1.392,1.392,2.5,2.542,4.679.36S14.815,10.5,13.423,9.1Z"></path>',
  '<line class="ql-stroke" x1="3" x2="15" y1="15" y2="3"></line>',
  '</svg>',
].join('');
icons.image = [
  '<svg viewbox="0 0 24 24">',
  '<rect class="ql-stroke" fill="none" width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>',
  '<circle class="ql-stroke" fill="none" cx="9" cy="9" r="2"></circle>',
  '<path class="ql-stroke" fill="none" d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>',
  '</svg>',
].join('');
icons.upload = [
  '<svg viewbox="0 0 24 24">',
  '<path class="ql-stroke" fill="none" d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>',
  '<polyline class="ql-stroke" fill="none" points="17 8 12 3 7 8"></polyline>',
  '<line class="ql-stroke" x1="12" x2="12" y1="3" y2="15"></line>',
  '</svg>',
].join('');

const SCHEME = /^(https?:\/\/|mailto:|tel:)/i;

function normalizeLinkUrl(raw) {
  const url = (raw || '').trim();
  if (!url || /^(javascript|data):/i.test(url)) {
    return '';
  }
  if (SCHEME.test(url)) {
    return url.replace(SCHEME, '').trim() ? url : '';
  }
  if (/^\S+@\S+\.\S+$/.test(url)) {
    return `mailto:${url}`;
  }
  // A leading + can't start a URL, so treat it as a phone rather than prefixing https://.
  if (/^\+[\d\s\-().]+$/.test(url)) {
    return `tel:${url.replace(/[\s\-().]/g, '')}`;
  }
  return `https://${url}`;
}

const TIP_MAX_WIDTH = 260;
const TIP_GAP = 6;
// Below this much room under the link, the tooltip would be clipped, so it flips above instead.
const TIP_FLIP_THRESHOLD = 60;

function anchorFromEvent(event) {
  return (event.target.closest && event.target.closest('a')) || null;
}

function linkTipLabel(url) {
  return url.replace(SCHEME, '').replace(/\/+$/, '');
}
 
if (!Quill.imports['formats/briefVideo']) {
  const BlockEmbed = Quill.import('blots/block/embed');
  class BriefVideoBlot extends BlockEmbed {
    static create(url) {
      const node = super.create();
      node.setAttribute('src', url);
      node.setAttribute('controls', 'controls');
      node.setAttribute('preload', 'metadata');
      node.setAttribute('playsinline', 'playsinline');
      return node;
    }

    static value(node) {
      return node.getAttribute('src');
    }
  }
  BriefVideoBlot.blotName = 'briefVideo';
  BriefVideoBlot.tagName = 'VIDEO';
  Quill.register(BriefVideoBlot);
}

function editorLooksBlank(root) {
  if (!root) {
    return true;
  }
  if (root.querySelector(`img, video, iframe, .ql-video, .${IMAGE_TYPE_TAG_CLASS}`)) {
    return false;
  }
  return !(root.textContent || '').replace(/\u00a0/g, ' ').trim();
}

const PASTE_STYLE_KEYS = ['background', 'color', 'font', 'size'];

function stripPasteStylesFromDelta(delta) {
  if (!delta || !Array.isArray(delta.ops)) {
    return delta;
  }
  delta.ops.forEach((op) => {
    if (!op.attributes) {
      return;
    }
    PASTE_STYLE_KEYS.forEach((key) => {
      delete op.attributes[key];
    });
    if (!Object.keys(op.attributes).length) {
      delete op.attributes;
    }
  });
  return delta;
}

export default {
  name: 'RichTextEditor',
  components: { VueEditor, TextInputModal },
  props: {
    value: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    allowImages: {
      type: Boolean,
      default: false,
    },
    allowLibraryBrowse: {
      type: Boolean,
      default: true,
    },
    // Off when the host owns the resize grip so its own bottom-right corner is the only one.
    resizable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      quill: null,
      unlinkButton: null,
      uploadButton: null,
      pendingLink: null,
      linkTip: null,
      tipHideTimer: null,
    };
  },
  computed: {
    toolbar() {
      const base = ['bold', 'italic', 'underline', { list: 'bullet' }, { list: 'ordered' }, 'link', 'unlink'];
      if (this.allowImages) {
        if (this.allowLibraryBrowse) {
          base.push('image');
        }
        base.push('upload');
      }
      return [base];
    },
    // maxWidth comes from the same constant the horizontal clamp uses, so the two cannot drift.
    tipStyle() {
      return this.linkTip && {
        left: `${this.linkTip.left}px`,
        top: `${this.linkTip.top}px`,
        maxWidth: `${TIP_MAX_WIDTH}px`,
      };
    },
  },
  watch: {
    value() {
      this.$nextTick(() => this.syncBlankClass());
    },
  },
  beforeDestroy() {
    if (this.unlinkButton) {
      this.unlinkButton.removeEventListener('click', this.onUnlinkClick);
      this.unlinkButton = null;
    }
    if (this.uploadButton) {
      this.uploadButton.removeEventListener('click', this.onUploadClick);
      this.uploadButton = null;
    }
    if (this.quill) {
      this.quill.off('selection-change', this.onSelectionChange);
      this.quill.root.removeEventListener('mouseover', this.onEditorMouseOver);
      this.quill.root.removeEventListener('mouseout', this.onEditorMouseOut);
      this.quill.off('text-change', this.hideLinkTip);
      this.quill.root.removeEventListener('scroll', this.hideLinkTip);
      this.quill = null;
    }
    clearTimeout(this.tipHideTimer);
  },
  methods: {
    onReady(quill) {
      this.quill = quill;
      this.setupPasteSanitizer(quill);
      this.clearLoadedPresentationStyles(quill);
      this.setupLinkTools(quill);
      this.setupMediaTools(quill);
      this.$nextTick(() => this.syncBlankClass());
      this.$emit('ready', quill);
    },
    setupPasteSanitizer(quill) {
      const clipboard = quill.getModule('clipboard');
      if (!clipboard || typeof clipboard.addMatcher !== 'function') {
        return;
      }
      clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
        if (node.tagName === 'SPAN' && node.classList && node.classList.contains(IMAGE_TYPE_TAG_CLASS)) {
          const value = imageTypeTagValueFromNode(node);
          return value ? new Delta().insert({ [IMAGE_TYPE_TAG_BLOT]: value }) : delta;
        }
        return stripPasteStylesFromDelta(delta);
      });
    },
    clearLoadedPresentationStyles(quill) {
      const length = quill.getLength();
      if (length <= 1) {
        return;
      }
      quill.formatText(0, length, {
        background: false,
        color: false,
        font: false,
        size: false,
      }, 'silent');
    },
    setupLinkTools(quill) {
      const toolbar = quill.getModule('toolbar');
      if (!toolbar) {
        return;
      }
      toolbar.addHandler('link', this.onLinkClick);
      // Toolbar.attach() skips unknown formats like `unlink`, so its click never reaches a handler.
      this.unlinkButton = toolbar.container.querySelector('.ql-unlink');
      if (this.unlinkButton) {
        this.unlinkButton.addEventListener('click', this.onUnlinkClick);
      }
      quill.on('selection-change', this.onSelectionChange);
      // Links are inert inside contenteditable, so hovering is the only way to reach them.
      quill.root.addEventListener('mouseover', this.onEditorMouseOver);
      quill.root.addEventListener('mouseout', this.onEditorMouseOut);
      quill.on('text-change', this.hideLinkTip);
      quill.root.addEventListener('scroll', this.hideLinkTip);
      this.refreshLinkState();
    },
    setupMediaTools(quill) {
      const toolbar = quill.getModule('toolbar');
      if (!toolbar || !this.allowImages) {
        return;
      }
      const imageBtn = toolbar.container.querySelector('.ql-image');
      if (imageBtn) {
        imageBtn.setAttribute('title', 'Insert image from library');
      }
      // `upload` is not a Quill format, so Toolbar.attach() ignores it — wire the click ourselves.
      this.uploadButton = toolbar.container.querySelector('.ql-upload');
      if (this.uploadButton) {
        this.uploadButton.setAttribute('title', 'Upload image from computer');
        this.uploadButton.addEventListener('click', this.onUploadClick);
      }
    },
    onUploadClick() {
      if (this.disabled || !this.quill) {
        return;
      }
      // Match Quill toolbar buttons: focus before the action. vue2-editor resets HTML when
      // unfocused if the bound value lags the DOM, which drops embeds before autosave.
      this.quill.focus();
      this.$emit('pick-upload');
    },
    onEditorMouseOver(event) {
      const anchor = anchorFromEvent(event);
      if (!anchor || !this.quill.root.contains(anchor)) {
        return;
      }
      const url = anchor.getAttribute('href') || '';
      if (!url) {
        return;
      }
      this.cancelTipHide();
      const rect = anchor.getBoundingClientRect();
      const host = this.$el.getBoundingClientRect();
      const above = window.innerHeight - rect.bottom < TIP_FLIP_THRESHOLD;
      this.linkTip = {
        url,
        above,
        label: linkTipLabel(url),
        left: Math.max(0, Math.min(rect.left - host.left, host.width - TIP_MAX_WIDTH)),
        top: above ? rect.top - host.top - TIP_GAP : rect.bottom - host.top + TIP_GAP,
      };
    },
    onEditorMouseOut(event) {
      if (!anchorFromEvent(event)) {
        return;
      }
      clearTimeout(this.tipHideTimer);
      // Delayed so the pointer can travel from the link onto the tooltip without it vanishing.
      this.tipHideTimer = setTimeout(this.hideLinkTip, 200);
    },
    cancelTipHide() {
      clearTimeout(this.tipHideTimer);
    },
    hideLinkTip() {
      clearTimeout(this.tipHideTimer);
      this.linkTip = null;
    },
    onSelectionChange(range) {
      if (range) {
        this.refreshLinkState(range);
      }
    },
    refreshLinkState(range) {
      if (!this.unlinkButton) {
        return;
      }
      const current = range || this.quill.getSelection();
      this.unlinkButton.disabled = !(current && this.linkAt(current).url);
    },
    // Toolbar clicks move focus out of the editor; focus() restores the range Quill saved on blur.
    targetAtCursor() {
      this.quill.focus();
      const range = this.quill.getSelection();
      return range ? this.linkAt(range) : null;
    },
    // descendant() also matches the position just before a link, so a bare caret must be inside it.
    linkAt(range) {
      const [blot, offset] = this.quill.scroll.descendant(LinkBlot, range.index);
      const url = blot ? LinkBlot.formats(blot.domNode) : '';
      if (range.length) {
        return { index: range.index, length: range.length, url };
      }
      if (!blot || offset === 0) {
        return { index: range.index, length: 0, url: '' };
      }
      return { index: range.index - offset, length: blot.length(), url };
    },
    onLinkClick() {
      if (this.disabled || !this.quill) {
        return;
      }
      this.pendingLink = this.targetAtCursor();
      if (!this.pendingLink) {
        return;
      }
      this.$refs.linkModal.open(this.pendingLink.url);
    },
    applyLink(raw) {
      const url = normalizeLinkUrl(raw);
      const target = this.pendingLink;
      this.pendingLink = null;
      if (!url || !target || !this.quill) {
        return;
      }
      const length = target.length || url.length;
      if (target.length) {
        this.quill.formatText(target.index, target.length, 'link', url, 'user');
      } else {
        this.quill.insertText(target.index, url, { link: url }, 'user');
      }
      // A caret past the link's last character reads as outside it, so leave the new link selected.
      const applied = { index: target.index, length };
      this.quill.setSelection(applied.index, applied.length, 'silent');
      this.refreshLinkState(applied);
    },
    onUnlinkClick() {
      const target = this.disabled ? null : this.targetAtCursor();
      if (target && target.url) {
        this.quill.formatText(target.index, target.length, 'link', false, 'user');
        this.refreshLinkState();
      }
    },
    syncBlankClass() {
      const root = this.quill && this.quill.root;
      if (!root) {
        return;
      }
      root.classList.toggle('ql-blank', editorLooksBlank(root));
    },
    onInput(html) {
      this.$emit('input', html);
      this.syncBlankClass();
    },
  },
};
</script>

<style scoped>
.rich-text-editor {
  width: 100%;
  position: relative;
}

.rich-text-editor__tip {
  position: absolute;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: hsl(0, 0%, 100%);
  border: 1px solid hsl(214.3, 31.8%, 91.4%);
  border-radius: 4px;
  box-shadow: 0 2px 8px hsla(222.2, 47.4%, 11.2%, 0.12);
  font-size: 12px;
  line-height: 18px;
}

.rich-text-editor__tip--above {
  transform: translateY(-100%);
}

.rich-text-editor__tip-url {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: hsl(215.4, 16.3%, 46.9%);
}

.rich-text-editor__tip-open {
  display: inline-flex;
  color: hsl(162, 73%, 35%);
}

.rich-text-editor__tip-open:hover {
  color: hsl(162, 73%, 30%);
}

.rich-text-editor ::v-deep .ql-snow.ql-toolbar {
  border-top-left-radius: 0.375rem;
  border-top-right-radius: 0.375rem;
  border-color: hsl(214.3 31.8% 91.4%);
  padding-top: 5px;
  padding-bottom: 4px;
}

.rich-text-editor ::v-deep .ql-toolbar.ql-snow .ql-formats{
  display: flex;
  gap: 4px;
  margin-bottom: 0;
}

.rich-text-editor ::v-deep .ql-toolbar button {
  width: 26px;
  height: 26px;
  padding: 4px;
  border-radius: 0.25rem;
  transition: background-color 0.12s ease;
}

.rich-text-editor ::v-deep .ql-snow.ql-toolbar button svg {
  width: 18px;
  height: 18px;
}

.rich-text-editor ::v-deep .ql-toolbar button .ql-stroke {
  stroke: hsl(215.4 16.3% 46.9%);
}

.rich-text-editor ::v-deep .ql-toolbar button .ql-fill {
  fill: hsl(215.4 16.3% 46.9%);
}

.rich-text-editor ::v-deep .ql-toolbar button:hover,
.rich-text-editor ::v-deep .ql-toolbar button:focus,
.rich-text-editor ::v-deep .ql-toolbar button.ql-active {
  background-color: hsl(210 40% 94.1%);
  color: hsl(222.2 47.4% 11.2%);
}

.rich-text-editor ::v-deep .ql-toolbar button:hover .ql-stroke,
.rich-text-editor ::v-deep .ql-toolbar button:focus .ql-stroke,
.rich-text-editor ::v-deep .ql-toolbar button.ql-active .ql-stroke {
  stroke: hsl(222.2 47.4% 11.2%);
}

.rich-text-editor ::v-deep .ql-toolbar button:hover .ql-fill,
.rich-text-editor ::v-deep .ql-toolbar button:focus .ql-fill,
.rich-text-editor ::v-deep .ql-toolbar button.ql-active .ql-fill {
  fill: hsl(222.2 47.4% 11.2%);
}

.rich-text-editor ::v-deep .ql-container {
  border-bottom-left-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
  border-color: hsl(214.3 31.8% 91.4%);
  font-family: inherit;
  font-size: 0.875rem;
}

.rich-text-editor ::v-deep .ql-editor {
  min-height: 8rem;
  margin-bottom: 10px;
}

.rich-text-editor--resizable ::v-deep .ql-editor {
  resize: vertical;
}

.rich-text-editor--resizable ::v-deep .ql-disabled .ql-editor {
  resize: none;
}

.rich-text-editor ::v-deep .ql-editor img,
.rich-text-editor ::v-deep .ql-editor video {
  max-width: 100%;
  max-height: 240px;
  border-radius: calc(0.375rem - 2px);
}

.rich-text-editor ::v-deep .ql-editor ol,
.rich-text-editor ::v-deep .ql-editor ul {
  margin: 0.25rem 0;
  padding-left: 1.5rem;
}

.rich-text-editor ::v-deep .ql-editor ol {
  list-style-type: decimal;
}

.rich-text-editor ::v-deep .ql-editor ul {
  list-style-type: disc;
}

.rich-text-editor ::v-deep .ql-editor li {
  padding-left: 0.25rem;
}

.rich-text-editor ::v-deep .ql-editor li + li {
  margin-top: 0.125rem;
}

.rich-text-editor ::v-deep .ql-editor a {
  color: hsl(162, 73%, 35%);
  text-decoration: underline;
}

.rich-text-editor ::v-deep .ql-toolbar button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.rich-text-editor ::v-deep .ql-toolbar button:disabled:hover {
  background-color: transparent;
}

/* Snow pops its own link tooltip when the caret enters a link; the modal is the single link UI. */
.rich-text-editor ::v-deep .ql-tooltip {
  display: none;
}

.rich-text-editor ::v-deep .ql-editor.ql-blank::before {
  font-style: normal;
  color: hsl(215.4 16.3% 46.9%);
}

/* Pasted/imported spans often keep a solid background; keep text on the editor surface. */
.rich-text-editor ::v-deep .ql-editor *:not(img):not(video):not(.brief-image-type-tag) {
  background-color: transparent !important;
  background-image: none !important;
}

.rich-text-editor ::v-deep .ql-editor .brief-image-type-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  margin: 2px 4px 2px 0;
  border-radius: 9999px;
  background: hsl(162 73% 35% / 0.12) !important;
  color: hsl(162 73% 25%);
  border: 1px solid hsl(162 73% 35% / 0.35);
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  vertical-align: middle;
  white-space: nowrap;
  user-select: none;
  cursor: default;
}
</style>
